import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Connection, LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js';
import * as multisig from '@sqds/multisig';
import { useMultisigStore } from '@/stores/multisig';
import { useWalletStore } from '@/stores/wallet';
import { useWalletConnectStore } from '@/stores/walletConnect';
import { initWalletConnect } from '@/lib/walletConnect';
import { getConfig } from '@/lib/config';
import { env } from '@/lib/env';
import AddressRow from '@/components/AddressRow';

export default function Dashboard() {
  const pubkey = useWalletStore((s) => s.pubkey);
  const activePda = useMultisigStore((s) => s.activePda);
  const pending = useWalletConnectStore((s) => s.pending);

  const [vaultAddr, setVaultAddr] = useState<string | null>(null);
  const [vaultBalance, setVaultBalance] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const isConnected = pubkey !== null;

  const refresh = useCallback(async () => {
    if (!activePda) return;
    setRefreshing(true);
    try {
      const cfg = await getConfig();
      const programId = new PublicKey(cfg.programId);
      const [vault] = multisig.getVaultPda({
        multisigPda: new PublicKey(activePda),
        index: 0,
        programId,
      });
      setVaultAddr(vault.toBase58());
      const conn = new Connection(env.solanaRpcUrl, 'confirmed');
      const lamports = await conn.getBalance(vault, 'confirmed');
      setVaultBalance(lamports / LAMPORTS_PER_SOL);
    } catch (err) {
      console.error('refresh failed', err);
    } finally {
      setRefreshing(false);
    }
  }, [activePda]);

  useEffect(() => {
    if (isConnected) void initWalletConnect();
  }, [isConnected]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div className="space-y-6">
      {!isConnected ? (
        <div className="rounded-xl border border-ink-800 bg-ink-900/40 p-8 text-center">
          <h2 className="font-display text-xl mb-2">Connect your wallet to begin</h2>
          <p className="text-sm text-ink-300">
            Use the button in the top right to connect any Solana wallet.
          </p>
        </div>
      ) : !activePda ? (
        <div className="rounded-xl border border-ink-800 bg-ink-900/40 p-8 text-center">
          <h2 className="font-display text-xl mb-2">No multisig yet</h2>
          <p className="text-sm text-ink-300 mb-4">
            Create your 2-of-2 multisig to start using 2FA-gated transactions.
          </p>
          <Link
            to="/app/setup"
            className="inline-block rounded-lg bg-avo-500 px-5 py-2.5 text-sm font-medium text-ink-950 hover:bg-avo-400 transition-colors"
          >
            Create multisig
          </Link>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-ink-800 bg-ink-900/40 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs text-ink-400 uppercase tracking-wider">
                  Vault balance
                </p>
                <p className="font-display text-3xl font-semibold mt-1">
                  {vaultBalance !== null ? vaultBalance.toFixed(4) : '—'}{' '}
                  <span className="text-base text-ink-300">SOL</span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => void refresh()}
                disabled={refreshing}
                className="rounded-lg border border-ink-800 bg-ink-900/60 px-3 py-1.5 text-xs text-ink-100 hover:bg-ink-800/60 transition-colors"
              >
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
            <div className="space-y-2">
              <div>
                <p className="text-[11px] text-ink-500 uppercase tracking-wider mb-1">
                  Receive address (vault)
                </p>
                {vaultAddr ? (
                  <AddressRow label="Vault" address={vaultAddr} />
                ) : (
                  <p className="text-xs text-ink-500">Resolving vault address…</p>
                )}
              </div>
              <div className="pt-2 border-t border-ink-800/60">
                <p className="text-[11px] text-ink-500 uppercase tracking-wider mb-1">
                  Multisig ID (config — do not send funds here)
                </p>
                <AddressRow label="Multisig ID" address={activePda} showQr={false} />
              </div>
            </div>
            <p className="mt-3 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-300/80 leading-relaxed">
              Always send SOL or tokens to the{' '}
              <span className="font-medium text-amber-200">vault</span>. The Multisig ID
              is a program-owned config account — sending to it returns{' '}
              <span className="font-mono">invalid account</span> in any wallet.
            </p>
          </div>

          <div className="rounded-xl border border-ink-800 bg-ink-900/40 p-6">
            <h3 className="font-semibold text-ink-50 mb-3">Pending requests</h3>
            {pending.length === 0 ? (
              <div className="text-sm text-ink-400">
                No pending requests. Connect a dApp from the{' '}
                <Link
                  to="/app/connections"
                  className="text-avo-400 hover:text-avo-300"
                >
                  Connections
                </Link>{' '}
                page.
              </div>
            ) : (
              <ul className="space-y-2">
                {pending.map((r) => (
                  <li
                    key={r.id}
                    className="rounded-lg border border-ink-800 bg-ink-900 px-4 py-3 flex items-center justify-between text-sm"
                  >
                    <div>
                      <div className="text-ink-100">{r.method}</div>
                      <div className="text-xs text-ink-400">
                        {r.origin ?? 'unknown origin'}
                      </div>
                    </div>
                    <Link
                      to={`/app/transactions/${r.id}`}
                      className="rounded-lg bg-avo-500 px-3 py-1.5 text-xs font-medium text-ink-950 hover:bg-avo-400 transition-colors"
                    >
                      Review
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Link
              to="/app/transactions"
              className="rounded-xl border border-ink-800 bg-ink-900/40 p-5 hover:bg-ink-800/40 transition-colors"
            >
              <div className="text-xs text-ink-400 uppercase tracking-wider mb-1">
                Transactions
              </div>
              <div className="text-ink-100">View past + pending vault txs</div>
            </Link>
            <Link
              to="/app/settings"
              className="rounded-xl border border-ink-800 bg-ink-900/40 p-5 hover:bg-ink-800/40 transition-colors"
            >
              <div className="text-xs text-ink-400 uppercase tracking-wider mb-1">
                Settings
              </div>
              <div className="text-ink-100">TOTP, sessions, members</div>
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
